import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { NgxMaskDirective } from "ngx-mask";
import {
  Subject,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  take,
} from "rxjs";

import { ActivatedRoute, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  CorpPayor,
  CorpPayorDropdowns,
} from "src/app/common/interfaces/corp-payor.interface";
import { CorpPayorClient } from "src/app/common/services/corp-payor.client";
import { StorageService } from "src/app/common/services/storage.service";
import { ChangeTracker } from "../../../common/classes/change-tracker.class";
import {
  ButtonComponent,
  ButtonVariant,
} from "../../../common/components/core/button/button.component";
import { DropdownComponent } from "../../../common/components/core/dropdown/dropdown.component";
import { InputComponent } from "../../../common/components/core/input/input.component";
import { ConfirmationModalComponent } from "../../../common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { ClientSideListBaseComponent } from "../../../common/core/client-side-list-base/client-side-list-base.component";
import { EntityManagementLayoutComponent } from "../../../common/layouts/entity-management-layout.component";
import { LookupClient } from "../../../common/services/lookup.client";
import { AtLeastOneRequired } from "../../../common/validators/at-least-one-requied.validator";
import { lcdColumnConfig } from "./lcd-config";
import { PayorStore } from "./payor-store.service";
import { differenceInDays, parse, parseISO } from "date-fns";
import { now } from "lodash";
import { MatSelectChange } from "@angular/material/select";
import { PayorInUseComponent } from "./modal/payor-in-use/payor-in-use.component";
import { ChangesSaved, DialogMessageUtility } from "src/app/common/constants/core-constants";
import { DropdownValues } from "src/app/common/enums/Add-payor-plan.enum";
import { DailyNoteChoice } from "src/app/common/enums/dailyNoteChoice.enum";

@UntilDestroy()
@Component({
  selector: "app-payor-form",
  templateUrl: "./payor-form-management.component.html",
  standalone: true,
  imports: [
    CommonModule,
    EntityManagementLayoutComponent,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    NgxMaskDirective,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    ButtonComponent,
    ClientSideListBaseComponent,
  ],
})
export class PayorFormComponent extends ChangeTracker implements OnInit {
  payorForm!: FormGroup;
  title!: string;
  payor!: CorpPayor;
  reload$ = new Subject<void>();
  levelOfCareColumns!: any[];
  lcdColumnConfig = lcdColumnConfig(this);
  buttonVariant = ButtonVariant;
  payorDropdowns: CorpPayorDropdowns | undefined = undefined;
  dropdownDisabled : boolean = false;
  statusDropdown = [
    {
      id: "E",
      name: "Active",
    },
    {
      id: "I",
      name: "Inactive",
    },
  ];

  constructor(
    private formBuilder: FormBuilder,
    public lookupClient: LookupClient,
    private dialog: MatDialog,
    private payorStore: PayorStore,
    private payorClient: CorpPayorClient,
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService,
    private router: Router
  ) {
    super();
  }

  async ngOnInit() {
    this.payorForm = this.formBuilder.group({
      id: [null],
      status: ["A"],
      payorname: [null, Validators.required],
      facilityid: [null],
      corpid: [null],
      payingagent: [""],
      payortypeid: [null],
      abbreviation: [null, [Validators.required, Validators.maxLength(20)]],
      address1: [null, Validators.required],
      address2: [null],
      city: [null, Validators.required],
      stateid: [null, Validators.required],
      zipcode: [null, Validators.required],
      countyid: [null],
      primaryphone: [null],
      fax: [null],
      website: [null],
      contactname: [null],
      contactphone: [null],
      contactemail: [null, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)],
      comments: [null],
      payorOption: this.formBuilder.group({
        ppsTrackingGroup: this.formBuilder.group({
          changeomras: [false],
          endomras: [false],
          startomras: [false],
        }),
        weekdefinitionid: [null, Validators.required],
        ccivalidation: [null, Validators.required],
        dailynotesid: [2], //default to not required
        notePreferenceid: [DailyNoteChoice.NoNotesRequired, Validators.required], //default is null
        ppstracking: [null, Validators.required],
        rcdayscount: [null, [Validators.max(365), Validators.min(0), Validators.required]],
        prdayscount: [null, [Validators.max(365), Validators.min(0), Validators.required]],
        lcdruleid: [null],
        sectiongg: [false],
        allowconcurrenttherapy: [false],
        authorizationsrequired: [false],
        captracking: [false],
        concurrenttherapy: [false],
        xmodifiers: [false],
        grouptreatment: [false],
        requirepqrs: [false],
        allowcotreatment: [false],
        enforceduration: [false],
        mppradjusted: [false],
        requiremips: [false],
        levelofcareid: [null, Validators.required],
      }),
      payorOther: this.formBuilder.group({
        modifierkx: [false],
        modified52applies: [false],
        modified77applies: [false],
        assistantmodifier: [false],
      }),
      payorTeleHealth: this.formBuilder.group({
        sos02: [false],
        modifiercr: [false],
        codedr: [false],
        modifiergq: [false],
        rev780: [false],
        telehealthsynccodeid: [null],
      }),
    });
    this.payorStore.payor$
      .pipe(
        filter((x) => !!x),
        take(1)
      )
      .subscribe(async (payor) => {
        this.formGroup.registerControl("payorForm", this.payorForm);
        this.payor = payor as CorpPayor;

        this.payorForm.patchValue(this.payor);
        if (this.payor.payorOption.ppstracking !== undefined) {
          this.payorForm
            .get("payorOption")
            ?.get("ppstracking")
            ?.setValue(this.payor.payorOption.ppstracking ? DropdownValues.YES : DropdownValues.NO);
        }
        if (this.payor.payorOption.ccivalidation !== undefined) {
          this.payorForm
            .get("payorOption")
            ?.get("ccivalidation")
            ?.setValue(this.payor.payorOption.ccivalidation? DropdownValues.YES : DropdownValues.NO);
        }

        if (this.payor.payorOption.changeomras) {
          this.payorForm
            .get("payorOption")
            ?.get("ppsTrackingGroup")
            ?.get("changeomras")
            ?.setValue(this.payor.payorOption.changeomras);
        }

        if (this.payor.payorOption.endomras) {
          this.payorForm
            .get("payorOption")
            ?.get("ppsTrackingGroup")
            ?.get("endomras")
            ?.setValue(this.payor.payorOption.endomras);
        }

        if (this.payor.payorOption.startomras) {
          this.payorForm
            .get("payorOption")
            ?.get("ppsTrackingGroup")
            ?.get("startomras")
            ?.setValue(this.payor.payorOption.startomras);
        }

        const queryParams = await firstValueFrom(
          this.activatedRoute.queryParams
        );

        if (this.payor.corpid) {
          this.payorDropdowns = await firstValueFrom(
            this.payorClient.getPayorPlanDropdowns(this.payor.corpid)
          );
        }
      });

    if (this.payor?.createdtime) {
      const created = parseISO(this.payor.createdtime);
      created.setHours(0, 0, 0, 0);

      const now = new Date();
      now.setHours(0, 0, 0, 0);

      if (differenceInDays(now, created) > 14) {
        this.payorForm.disable();
        this.payorForm.get("status")?.enable();
        this.dropdownDisabled = true;
      }
    }

    setTimeout(() => this.reload$.next());

    setTimeout(() => {
      this.payorForm
        .get("stateid")
        ?.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
        .subscribe((value) => {
          setTimeout(() => {
            this.reload$.next();
          }, 200);
        });
    });

    if (!this.payorDropdowns) {
      const queryParams = await firstValueFrom(this.activatedRoute.queryParams);

      this.payorDropdowns = await firstValueFrom(
        this.payorClient.getPayorPlanDropdowns(queryParams["corporationId"])
      );
    }
  }

  // clears inputs dependent on pps tracking control
  ppsTrackingChange($event: any) {
    if ($event.value === DropdownValues.NO) {
      this.payorForm
        .get("payorOption")
        ?.get("ppsTrackingGroup")
        ?.setValidators(null);

      this.payorForm
        .get("payorOption")
        ?.get("ppsTrackingGroup")
        ?.setErrors(null);
      this.payorForm
        .get("payorOption")
        ?.get("ppsTrackingGroup")
        ?.setValue({
          changeomras: false,
          endomras: false,
          startomras: false,
        });
      this.payorForm.get("payorOption")?.get("changeomras")?.setValue(false);
      this.payorForm.get("payorOption")?.get("endomras")?.setValue(false);
      this.payorForm.get("payorOption")?.get("startomras")?.setValue(false);
    } else {
      this.payorForm
        .get("payorOption")
        ?.get("ppsTrackingGroup")
        ?.setValidators(AtLeastOneRequired());
    }
  }

  intelligentDocConfigValueChange(event : any) {
    if(event.value > 365) {
      this.payorForm.get("payorOption")?.get(event.control)?.setValue(null);
    }
  }

  cciValidationChange($event: any) {
    if ($event.value === DropdownValues.NO) {
      this.payorForm.get("payorOption")?.get("xmodifiers")?.setValue(false);
    }
  }

  async onSaveClicked(navigateBackToSettings: boolean = true, showConfirmation: boolean = true) {
    this.formGroup.markAllAsTouched();
    this.payorForm.markAllAsTouched();
    if (this.formGroup.valid && this.payorForm.valid) {
      try {
        const ppstracking = this.payorForm.get("payorOption")?.get("ppstracking")?.value;
        const ccivalidation = this.payorForm.get("payorOption")?.get("ccivalidation")?.value;
        const data = this.payorForm.getRawValue() as CorpPayor;
        data.payorOption.ccivalidation = this.isPPsTracking(ccivalidation);
        data.payorOption.ppstracking = this.isPPsTracking(ppstracking);
        const queryParams = await firstValueFrom(
          this.activatedRoute.queryParams
        );

        if (!data.corpid) {
          data.corpid = queryParams["corporationId"];
        }

        if (data.abbreviation) {
          data.abbreviation = data.abbreviation.toUpperCase();
        }

        if (!data.facilityid) {
          data.facilityid =
            this.storageService.getSelectedFacilityId()?.toString() || "";
        }

        const groupedPPS = (data.payorOption as any)?.ppsTrackingGroup || {};

        data.payorOption = {
          ...data.payorOption,
          ...groupedPPS,
        };

        delete (data.payorOption as any).ppsTrackingGroup;

        if (data.id) {
          await firstValueFrom(this.payorClient.editPayor(data));
          this.saveSuccess()
            .afterClosed()
            .subscribe(() => {
              if (navigateBackToSettings) {
                this.navigateBack();
              }
            });
        } else {
          const queryParams = await firstValueFrom(
            this.activatedRoute.queryParams
          );

          const result = (
            (await firstValueFrom(this.payorClient.addPayor(data))) as any
          ).responseData;

          this.formGroup.markAsPristine();
          this.payorForm.markAsPristine();
          if(showConfirmation) {
            this.dialog
            .open(ConfirmationModalComponent, {
              data: {
                title: "Changes Saved",
                message:
                  "New plans may be edited for 14 days. Edits will not be allowed after 14 days.",
                buttonText: "Continue",
                isError: false,
                hideCancelButton: true,
              },
            })
            .afterClosed()
            .subscribe(async () => {
              if (navigateBackToSettings) {
                this.router.navigate(["payor", result, "settings"], {
                  queryParams: {
                    corporationId: queryParams["corporationId"],
                  },
                });
              }
            });
          }
        }
      } catch (error: any) {
         await firstValueFrom(this.dialog.open(ConfirmationModalComponent, {
          data: {
            title: "Error",
            message: error?.error,
            buttonText: "OK",
            hideCancelButton: true,
            isError: true,
          },
        }).afterClosed());
      }
    }
  }
  isSelected(value: DropdownValues | null) {
    return value === DropdownValues.YES;
   }
   isPPsTracking(isPPSSelected : DropdownValues | boolean | null): boolean {
     return isPPSSelected === true || isPPSSelected === DropdownValues.YES;
   }
  async navigateBack() {
    const queryParams = await firstValueFrom(this.activatedRoute.queryParams);

    this.router.navigate(
      ["corporation", queryParams["corporationId"], "settings"],
      { queryParams: { view: "payor" } }
    );
  }

  saveSuccess() {
    this.formGroup.markAsPristine();
    this.payorForm.markAsPristine();
    return this.dialog.open(ConfirmationModalComponent, {
      data: ChangesSaved,
    });
  }

  override saveAndContinue(): void {
    this.onSaveClicked(false, false);
  }

  public navigateToSection(section: string) {
    document.querySelector("#" + section)?.scrollIntoView();
  }

  async statusChanged($event: any) {
    const val = ($event as MatSelectChange).value;

    if (val === "I" && this.payor.id) {
      const data =
        (
          await firstValueFrom(
            this.payorClient.validateActivePatientsForPayor(this.payor.id)
          )
        )?.data || [];

      if (data.length) {
        this.dialog.open(PayorInUseComponent, {
          data: {
            inUsePlans: data,
          },
        });
        this.payorForm.get("status")?.setValue("E");
      } else {
        this.dialog.open(ConfirmationModalComponent, {
          data: {
            ...DialogMessageUtility("Disable","Payor"),
            method: () => this.onSaveClicked(false),
          },
        });
      }
    }
  }

  dailyNotesChange($event: any) {
    const selectedValue = $event.value;
    if (selectedValue === 2) {
      this.payorForm.get("payorOption")?.get("notePreferenceid")?.setValue(DailyNoteChoice.NoNotesRequired);
    } else {
      this.payorForm.get("payorOption")?.get("notePreferenceid")?.setValue(DailyNoteChoice.CPTNoteRequired);
    }
  }
}
